import { useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import getFirstSelectedAreaIdFromChips from 'helpers/getFirstSelectedAreaIdFromChips';
import { getSubareaFilterForInstances } from 'services/_filters/parsers/actionLibraryInstanceFilters';
import { getSubareaFilter } from 'services/_filters/parsers/actionTemplateFilters';
import type { AvailableFilters } from 'types/ActionLibrary';

type SetAvailableFilters = Dispatch<SetStateAction<AvailableFilters | null>>;

/**
 * Keeps `availableFilters.subarea` in sync with the selected area (same pattern as Action Library Instance list).
 *
 * When no area is selected, we must load the default instances subarea only once per "empty area" stint.
 * Otherwise `availableFilters` is in the dependency array and each `setAvailableFilters` re-runs the effect,
 * re-calling `getSubareaFilterForInstances()` forever and freezing the UI thread.
 */
const useSyncSubareaFilterWithArea = (
  filtersArea: unknown[] | undefined,
  availableFilters: AvailableFilters | null,
  setAvailableFilters: SetAvailableFilters,
) => {
  const subareaLoadedForAreaRef = useRef<string | null>(null);
  /** Prevents infinite setState loop when `filtersArea` is empty and `availableFilters` updates this effect. */
  const emptyAreaDefaultSubareaSyncedRef = useRef(false);

  useEffect(() => {
    if (!filtersArea?.length) {
      subareaLoadedForAreaRef.current = null;
      if (!availableFilters) return;
      if (emptyAreaDefaultSubareaSyncedRef.current) return;
      emptyAreaDefaultSubareaSyncedRef.current = true;
      getSubareaFilterForInstances()
        .then(subData => {
          setAvailableFilters(prev =>
            prev ? ({ ...prev, subarea: { ...subData, isSingleSelect: true } } as unknown as AvailableFilters) : prev,
          );
        })
        .catch(() => {
          emptyAreaDefaultSubareaSyncedRef.current = false;
        });
      return;
    }

    emptyAreaDefaultSubareaSyncedRef.current = false;

    if (!availableFilters) return;
    const areaId = getFirstSelectedAreaIdFromChips(filtersArea);
    if (!areaId) return;
    if (subareaLoadedForAreaRef.current === areaId) return;
    subareaLoadedForAreaRef.current = areaId;
    getSubareaFilter(areaId)
      .then(subData => {
        setAvailableFilters(prev =>
          prev ? ({ ...prev, subarea: { ...subData, isSingleSelect: true } } as unknown as AvailableFilters) : prev,
        );
      })
      .catch(() => {});
  }, [availableFilters, filtersArea, setAvailableFilters]);
};

export default useSyncSubareaFilterWithArea;
